/**
 * Tab Coordination Manager
 * Manages multi-tab state synchronization, message handling, and session coordination
 * This class encapsulates all BroadcastChannel logic previously in App.tsx
 * Author: fabrice.miras@vitecsoftware.com
 * Date: 2025-10-10
 */

import { Dispatch } from 'redux';
import { tabCommunication, BroadcastMessage } from './TabCommunicationService';
import { navigate } from './navigationService';
import { tokenRefreshService } from './TokenRefreshService';
import * as actions from '../store/actions/index';
import Utils from './Utils';

export interface TabInfo {
    tabId: string;
    Logged: number;
    loginStateUI: number;
    isActive: boolean;
}

export interface TabCoordinationConfig {
    dispatch: Dispatch;
    tabId: string;
    isLogged: number;
    loginStateUI: number;
    transId?: string;
    isAutodataLoginInProgress?: boolean;
}

export class TabCoordinationManager {
    private tabIdList: TabInfo[] = [];
    private activeTabId = '';
    private config: TabCoordinationConfig;
    private messageHandler: ((message: BroadcastMessage) => void) | null = null;

    constructor(config: TabCoordinationConfig) {
        this.config = config;
    }

    /**
     * Update configuration (call this when props change)
     */
    updateConfig(config: Partial<TabCoordinationConfig>): void {
        this.config = { ...this.config, ...config };
    }

    /**
     * Initialize tab coordination - sets up message handlers and broadcasts initial state
     */
    initialize(): void {
        const { dispatch, tabId, isLogged, loginStateUI } = this.config;

        // Register message handler
        this.messageHandler = (message: BroadcastMessage) => {
            this.handleBroadcastMessage(message);
        };
        tabCommunication.onMessage('*', this.messageHandler);

        // Initialize current tab in list
        const currentTabExists = this.tabIdList.find(tab => tab.tabId === tabId);
        if (!currentTabExists) {
            this.tabIdList.push({ tabId, Logged: isLogged, loginStateUI, isActive: true });
        }

        // Broadcast current tab state
        this.broadcastTabState();

        // Deactivate all other tabs
        tabCommunication.broadcast({
            command: 'deactivate_all_tabs',
            timeStamp: new Date().getTime().toString() + Math.random()
        });

        // Set this tab as active
        this.activeTabId = tabId;
        dispatch(actions.setTabAsActive(true));
        this.tabIdList.forEach(tab => {
            tab.isActive = tab.tabId === tabId;
        });

        // Request other tabs to identify themselves
        this.requestTabList();

        // Request session storage if empty and not logged out
        if (!sessionStorage.length && isLogged !== 0) {
            tabCommunication.requestSessionStorage();
        }

        // Update Redux with tab info
        this.updateReduxTabState();
    }

    /**
     * Handle tab state update when login state changes
     */
    updateTabState(isLogged: number, loginStateUI: number): void {
        const { tabId } = this.config;
        const currentTabIndex = this.tabIdList.findIndex(tab => tab.tabId === tabId);

        // If current tab logged in, mark ALL tabs as logged in
        if (isLogged === 1) {
            Utils.log('✅ Tab logged in - Marking ALL tabs as logged in');
            this.tabIdList.forEach(tab => {
                tab.Logged = 1;
                tab.loginStateUI = loginStateUI;
            });

            if (currentTabIndex >= 0) {
                this.tabIdList[currentTabIndex].isActive = true;
                this.tabIdList[currentTabIndex].loginStateUI = loginStateUI;
            } else {
                this.tabIdList.push({ tabId, Logged: 1, loginStateUI, isActive: true });
            }

            // Broadcast to refresh all other tabs
            Utils.log('📢 Broadcasting REFRESH_NOT_LOGGED_TABS to update other tabs');
            tabCommunication.broadcast({
                command: 'REFRESH_NOT_LOGGED_TABS',
                tabId,
                data: { loginStateUI },
                random: new Date().getTime().toString() + Math.random(),
            });
        } else {
            // Update only current tab
            if (currentTabIndex >= 0) {
                this.tabIdList[currentTabIndex].Logged = isLogged;
                this.tabIdList[currentTabIndex].loginStateUI = loginStateUI;
                this.tabIdList[currentTabIndex].isActive = true;
            } else {
                this.tabIdList.push({ tabId, Logged: isLogged, loginStateUI, isActive: true });
            }
        }

        // Broadcast updated state
        this.broadcastTabState();
        this.updateReduxTabState();
    }

    /**
     * Handle window focus event - rebuilds tab list
     */
    handleWindowFocus(): void {
        const { dispatch, tabId, isLogged } = this.config;

        Utils.log('🔄 WINDOW FOCUS EVENT - Rebuilding tab list');
        Utils.log('Tab list before clearing:', JSON.parse(JSON.stringify(this.tabIdList)));
        dispatch(actions.setTabListSize(this.tabIdList.length));

        // Check if this is the only tab and not logged in
        if (this.tabIdList.length === 1 && isLogged !== 1) {
            sessionStorage.clear();
            dispatch(actions.setSessionUIState(-1));
        }

        // Keep only current tab in list
        const currentTabIndex = this.tabIdList.findIndex(tab => tab.tabId === tabId);
        if (currentTabIndex >= 0) {
            const currentTab = this.tabIdList[currentTabIndex];
            this.tabIdList.length = 0;
            this.tabIdList.push(currentTab);
            Utils.log('Tab list after clearing (kept current tab):', JSON.parse(JSON.stringify(this.tabIdList)));
            Utils.log(this.tabIdList.length, 'tab(s) remain in the list.');
        }

        // Request other tabs to identify themselves
        this.requestTabList();
    }

    /**
     * Broadcast current tab state to all other tabs
     */
    private broadcastTabState(): void {
        const { tabId, isLogged, loginStateUI } = this.config;
        tabCommunication.broadcast({
            command: 'SEND_THIS_STAMP_TO_OTHER_TAB',
            tabId,
            isLogged,
            data: { loginStateUI },
            random: new Date().getTime().toString() + Math.random(),
        });
    }

    /**
     * Request all tabs to identify themselves
     */
    private requestTabList(): void {
        const { tabId, isLogged } = this.config;
        Utils.log('📡 Broadcasting ASK_OTHER_TAB_FOR_THEIR_TAB_ID to rebuild list');
        tabCommunication.broadcast({
            command: 'ASK_OTHER_TAB_FOR_THEIR_TAB_ID',
            tabId,
            isLogged,
            random: new Date().getTime().toString() + Math.random(),
        });
    }

    /**
     * Handle incoming broadcast messages
     */
    private handleBroadcastMessage(message: BroadcastMessage): void {
        const { dispatch, tabId, isLogged, transId, isAutodataLoginInProgress } = this.config;
        const token = sessionStorage.getItem('token') || '';

        // Handle deactivate_all_tabs command
        if (message.command === 'deactivate_all_tabs') {
            this.activeTabId = '';
            dispatch(actions.setTabAsActive(false));

            const currentTabIndex = this.tabIdList.findIndex(tab => tab.tabId === tabId);
            if (currentTabIndex >= 0) {
                this.tabIdList[currentTabIndex].isActive = false;
            }
        }

        // Handle SEND_THIS_STAMP_TO_OTHER_TAB - another tab announces its state
        if (message.command === 'SEND_THIS_STAMP_TO_OTHER_TAB') {
            if (message.tabId && message.isLogged !== undefined) {
                const existingTabIndex = this.tabIdList.findIndex(tab => tab.tabId === message.tabId);
                Utils.log('📨 Received SEND_THIS_STAMP_TO_OTHER_TAB from:', message.tabId, 'isLogged:', message.isLogged);

                // If a tab logs in, mark ALL tabs as logged in
                if (message.isLogged === 1) {
                    Utils.log('✅ Tab logged in - marking ALL tabs as logged in');
                    this.tabIdList.forEach(tab => {
                        tab.Logged = 1;
                        if (message.data?.loginStateUI !== undefined) {
                            tab.loginStateUI = message.data.loginStateUI;
                        }
                    });

                    if (existingTabIndex < 0) {
                        Utils.log('➕ Adding new tab to list:', message.tabId);
                        this.tabIdList.push({
                            tabId: message.tabId,
                            Logged: 1,
                            loginStateUI: message.data?.loginStateUI || -1,
                            isActive: false
                        });
                    }

                    // Broadcast to refresh all other tabs
                    Utils.log('📢 Broadcasting REFRESH_NOT_LOGGED_TABS to update other tabs');
                    tabCommunication.broadcast({
                        command: 'REFRESH_NOT_LOGGED_TABS',
                        tabId: message.tabId,
                        data: { loginStateUI: message.data?.loginStateUI },
                        random: new Date().getTime().toString() + Math.random(),
                    });
                } else {
                    // Update or add tab with its current state
                    if (existingTabIndex >= 0) {
                        Utils.log('🔄 Updating existing tab:', message.tabId);
                        this.tabIdList[existingTabIndex].Logged = message.isLogged;
                        if (message.data?.loginStateUI !== undefined) {
                            this.tabIdList[existingTabIndex].loginStateUI = message.data.loginStateUI;
                        }
                    } else {
                        Utils.log('➕ Adding new tab to list:', message.tabId);
                        this.tabIdList.push({
                            tabId: message.tabId,
                            Logged: message.isLogged,
                            loginStateUI: message.data?.loginStateUI || -1,
                            isActive: false
                        });
                    }
                }
            }
        }

        // Handle ASK_OTHER_TAB_FOR_THEIR_TAB_ID - respond with current tab info
        if (message.command === 'ASK_OTHER_TAB_FOR_THEIR_TAB_ID') {
            Utils.log('❓ Received ASK_OTHER_TAB_FOR_THEIR_TAB_ID - responding with my info');
            this.broadcastTabState();
        }

        // Handle REMOVE_TAB_FROM_TABLIST - remove closed tab
        if (message.command === 'REMOVE_TAB_FROM_TABLIST') {
            if (message.tabId) {
                const tabIndex = this.tabIdList.findIndex(tab => tab.tabId === message.tabId);
                if (tabIndex >= 0) {
                    Utils.log('🗑️ Removing tab from list:', message.tabId);
                    this.tabIdList.splice(tabIndex, 1);
                    Utils.log('Remaining tabs:', this.tabIdList.length);
                } else {
                    Utils.log('⚠️ Tab not found in list for removal:', message.tabId);
                }
            }
        }

        // Handle LOGOUT_ALL_TABS - another tab initiated logout, log out this tab too
        if (message.command === 'LOGOUT_ALL_TABS') {
            this.handleLogoutAllTabs();
            return; // Stop processing further messages after full logout
        }

        // Handle REFRESH_NOT_LOGGED_TABS - update UI when another tab logs in
        if (message.command === 'REFRESH_NOT_LOGGED_TABS') {
            Utils.log('🔄 Received REFRESH_NOT_LOGGED_TABS - Updating UI state');
            dispatch(actions.setSessionUIState(1));
        }

        // Handle TOKEN_REQUEST - respond with token if available
        if (message.command === 'TOKEN_REQUEST') {
            const currentToken = sessionStorage.getItem('token');
          

            // Only respond if this tab is logged in
            if (currentToken && currentToken.trim() !== '' && isLogged === 1) {
                Utils.log('✅ Have valid token, responding to TOKEN_REQUEST');

                // Gather session storage data
                const sessionData: Record<string, string> = {};
                for (let i = 0; i < sessionStorage.length; i++) {
                    const key = sessionStorage.key(i);
                    if (key) {
                        sessionData[key] = sessionStorage.getItem(key) || '';
                    }
                }

                tabCommunication.sendToken({
                    token: currentToken,
                   
                    sessionData: sessionData
                });
            } else {
                Utils.log('⚠️ Cannot respond to TOKEN_REQUEST - missing token or not logged in (isLogged=' + isLogged + ')');
            }
        }

        // Auto-login to autodata if transId and token available
        if (transId && token && isLogged !== 0 && !isAutodataLoginInProgress) {
            dispatch(actions.userAutologinToAutodataAuthGet(token, transId, false, 1));
        }

        // Update Redux with current tab state
        this.updateReduxTabState();
    }

    /**
     * Handle LOGOUT_ALL_TABS broadcast - another tab initiated logout,
     * perform full logout on this tab and show the login form
     */
    private handleLogoutAllTabs(): void {
        const { dispatch } = this.config;
        Utils.log('🔒 Received LOGOUT_ALL_TABS - performing full logout and showing login form');

        // Update config immediately to prevent stale isLogged=1 from triggering auto-login
        this.config.isLogged = 0;
        this.config.loginStateUI = -1;

        tokenRefreshService.stop();
        sessionStorage.clear();
        navigate(`${Utils.HOMEPAGE_CONTEXT}/home`);
        dispatch(actions.resetStore());
        dispatch(actions.onClear(0, -1, false, this.tabIdList.length, false));
    }

    /**
     * Update Redux store with current tab coordination state
     */
    private updateReduxTabState(): void {
        const { dispatch, tabId } = this.config;

        // Ensure current tab exists in list
        const currentTabExists = this.tabIdList.find(tab => tab.tabId === tabId);
        if (!currentTabExists) {
            this.tabIdList.push({
                tabId,
                Logged: this.config.isLogged,
                loginStateUI: this.config.loginStateUI,
                isActive: true
            });
        }

        // Check if already logged in from another tab
        let isAlreadyLoggedFromAnotherTab = false;
        for (const tab of this.tabIdList) {
            if (tab.tabId !== tabId && (tab.Logged === 1 || tab.Logged === 2)) {
                isAlreadyLoggedFromAnotherTab = true;
                break;
            }
        }

        dispatch(actions.setIsAlreadyLoggedFromAnotherTab(isAlreadyLoggedFromAnotherTab));
        dispatch(actions.setTabListSize(this.tabIdList.length));
    }

    /**
     * Handle beforeunload event - cleanup before tab closes
     */
    handleBeforeUnload(): void {
        const { tabId } = this.config;

        tabCommunication.broadcast({
            command: 'REMOVE_TAB_FROM_TABLIST',
            tabId,
            data: {
                timestamp: new Date().getTime(),
                reason: 'beforeunload'
            }
        });

        const tabIndex = this.tabIdList.findIndex(tab => tab.tabId === tabId);
        if (tabIndex >= 0) {
            this.tabIdList.splice(tabIndex, 1);
        }
    }

    /**
     * Get current tab list (for debugging)
     */
    getTabList(): TabInfo[] {
        return [...this.tabIdList];
    }

    /**
     * Cleanup - remove message handlers
     */
    destroy(): void {
        if (this.messageHandler) {
            tabCommunication.offMessage('*');
            this.messageHandler = null;
        }
    }
}
